import React, { useState, useEffect } from 'react';
import '../styles/erp.css'
import { 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Typography,
  Avatar,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
  Alert,
  IconButton,
  Paper,
  Fade,
  Menu,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Backdrop,
  Snackbar
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { 
  Edit, 
  Delete, 
  Add, 
  Save, 
  Print, 
  Email,
  Phone,
  LocationOn,
  Close,
  MoreHoriz,
  KeyboardArrowDown,
  SmartToy
} from '@mui/icons-material';
import ServiceFactory from '../services/ApiService';
import HttpClient from '../services/HttpClient';
// --- API CONFIGURATION ---
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:44397/api/';
const COMPANY_ID = process.env.REACT_APP_COMPANY_ID || '1';

// --- DYNAMIC CONFIGURATION ---
const PROFILE_CONFIGS = {
  customer: {
    entityName: 'Customer',
    tabs: [
      { id: 'all', label: 'All Profiles' },
      { id: 'info', label: 'Profile Information' },
      { id: 'contacts', label: 'Contacts' },
      { id: 'banking', label: 'Banking Details' },
      { id: 'tax', label: 'Tax & Compliance' }
    ],
    moreTabs: [
      { id: 'documents', label: 'Documents' },
      { id: 'ledger', label: 'Transaction Records' },
      { id: 'custom', label: 'Custom Fields' },
      { id: 'addresses', label: 'Addresses' },
      { id: 'notes', label: 'Notes' },
      { id: 'history', label: 'Change History' }
    ]
  },
  vendor: {
    entityName: 'Vendor',
    tabs: [
      { id: 'all', label: 'All Profiles' },
      { id: 'info', label: 'Profile Information' },
      { id: 'contacts', label: 'Contacts' },
      { id: 'banking', label: 'Banking Details' },
      { id: 'tax', label: 'Tax & Compliance' }
    ],
    moreTabs: [
      { id: 'documents', label: 'Documents' },
      { id: 'ledger', label: 'Transaction Records' },
      { id: 'custom', label: 'Custom Fields' },
      { id: 'addresses', label: 'Addresses' },
      { id: 'notes', label: 'Notes' },
      { id: 'history', label: 'Change History' }
    ]
  },
  employee: {
    entityName: 'Employee',
    tabs: [
      { id: 'all', label: 'All Profiles' },
      { id: 'info', label: 'Personal Information' },
      { id: 'employment', label: 'Employment Details' },
      { id: 'banking', label: 'Banking Details' },
      { id: 'tax', label: 'Tax Information' }
    ],
    moreTabs: [
      { id: 'documents', label: 'Documents' },
      { id: 'payroll', label: 'Payroll History' },
      { id: 'custom', label: 'Custom Fields' },
      { id: 'addresses', label: 'Addresses' },
      { id: 'notes', label: 'Performance Notes' },
      { id: 'history', label: 'Change History' }
    ]
  }
};

// --- API FUNCTIONS ---
const apiCall = async (url, options = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}${url}`, options);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.errors?.join(', ') || data.message || 'An unknown API error occurred.');
    }
    
    return { success: true, ...data };

  } catch (error) {
    console.error('API Error:', error);
    return { success: false, message: error.message, data: null };
  }
};

const profileAPI = {
   getAllProfiles(params = {}) {
    return HttpClient.get('Profile', {
      COMPANY_ID,
      page: params.page || 1,
      pageSize: params.pageSize || 25,
      search: params.search || '',
      sortBy: params.sortBy || '',
      sortDirection: params.sortDirection || 'asc'
    });
  },

  // ✅ FIX: Always include profileType parameter
  getProfileById(id, profileType) {
    if (!profileType) {
      console.error('profileType is required for getProfileById');
      return Promise.reject(new Error('profileType is required'));
    }
    return HttpClient.get(`Profile/${id}`, { profileType });
  },

  // Get profiles by type (Vendor, Customer, Employee)
  getProfilesByType(profileType, params = {}) {
    return HttpClient.get(`Profile/type/${profileType}`, {
      COMPANY_ID,
      page: params.page || 1,
      pageSize: params.pageSize || 25,
      search: params.search || '',
      sortBy: params.sortBy || '',
      sortDirection: params.sortDirection || 'asc'
    });
  },

  // ✅ FIX: Create profile with all required fields
  createProfile( profileData) {
    return HttpClient.post('Profile', {
      ...profileData,
      COMPANY_ID
    });
  },

  // ✅ FIX: Update profile with profileType parameter
  updateProfile(id, profileType, profileData) {
    if (!profileType) {
      console.error('profileType is required for updateProfile');
      return Promise.reject(new Error('profileType is required'));
    }
    return HttpClient.put(`Profile/${id}`, profileData, { profileType });
  },

  // Delete profile
  deleteProfile(id, profileType) {
    return HttpClient.delete(`Profile/${id}`, { profileType });
  },

  // Get profile transactions (ledger)
  getProfileTransactions(profileId, profileType, fromDate, toDate) {
    return HttpClient.get(`Profile/${profileId}/transactions`, {
      profileType,
      fromDate,
      toDate
    });
  },

  // Get profile statistics
  getProfileStatistics(profileId, profileType) {
    return HttpClient.get(`Profile/${profileId}/statistics`, {
      profileType
    });
  },

  // ✅ NEW: Get next code for profile
  getNextCode(profileType) {
    return HttpClient.get('Profile/next-code', {
      profileType,
      COMPANY_ID
    });
  }
};

// Custom Field Builder Modal
const CustomFieldBuilder = ({ open, onClose, onSave, entityType }) => {
  const [fields, setFields] = useState([{ label: '', type: 'text', options: '', targetTab: 'info' }]);

  // Get all available tabs for the current entity type
  const getAvailableTabs = () => {
    const config = PROFILE_CONFIGS[entityType];
    const allTabs = [...config.tabs, ...config.moreTabs];
    // Filter out 'all' and 'custom' tabs as they're not suitable for custom fields
    return allTabs.filter(tab => tab.id !== 'all' && tab.id !== 'custom');
  };

  const addField = () => {
    setFields([...fields, { label: '', type: 'text', options: '', targetTab: 'info' }]);
  };

  const removeField = (index) => {
    if (fields.length > 1) {
      setFields(fields.filter((_, i) => i !== index));
    }
  };

  const updateField = (index, key, value) => {
    const newFields = [...fields];
    newFields[index][key] = value;
    setFields(newFields);
  };

  const handleSave = () => {
    const validFields = fields.filter(f => f.label.trim());
    onSave(validFields);
    onClose();
    setFields([{ label: '', type: 'text', options: '', targetTab: 'info' }]);
  };

  const columns = [
    {
      accessorKey: 'label',
      header: 'Field Label',
      size: 200,
      Cell: ({ row }) => (
        <TextField
          fullWidth
          size="small"
          value={row.original.label}
          onChange={(e) => updateField(row.index, 'label', e.target.value)}
          placeholder="Enter field label"
        />
      )
    },
    {
      accessorKey: 'type',
      header: 'Field Type',
      size: 150,
      Cell: ({ row }) => (
        <Select
          fullWidth
          size="small"
          value={row.original.type}
          onChange={(e) => updateField(row.index, 'type', e.target.value)}
        >
          <MenuItem value="text">Text Input</MenuItem>
          <MenuItem value="dropdown">Dropdown</MenuItem>
          <MenuItem value="date">Date Picker</MenuItem>
          <MenuItem value="file">File Upload</MenuItem>
        </Select>
      )
    },
    {
      accessorKey: 'targetTab',
      header: 'Target Tab',
      size: 180,
      Cell: ({ row }) => (
        <Select
          fullWidth
          size="small"
          value={row.original.targetTab}
          onChange={(e) => updateField(row.index, 'targetTab', e.target.value)}
        >
          {getAvailableTabs().map((tab) => (
            <MenuItem key={tab.id} value={tab.id}>
              {tab.label}
            </MenuItem>
          ))}
        </Select>
      )
    },
    {
      accessorKey: 'options',
      header: 'Options (comma-separated)',
      Cell: ({ row }) => (
        <TextField
          fullWidth
          size="small"
          value={row.original.options}
          onChange={(e) => updateField(row.index, 'options', e.target.value)}
          placeholder="Option1, Option2, Option3"
          disabled={row.original.type !== 'dropdown'}
        />
      )
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 100,
      Cell: ({ row }) => (
        <Button
          onClick={() => removeField(row.index)}
          color="error"
          size="small"
          variant="outlined"
        >
          Remove
        </Button>
      )
    }
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add Custom Fields</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Configure Fields
          </Typography>
          <Button 
            onClick={addField} 
            variant="outlined" 
            size="small"
            startIcon={<Add />}
          >
            Add Field
          </Button>
        </Box>
        <MaterialReactTable
          columns={columns}
          data={fields}
          enableColumnOrdering
          enableColumnResizing
          enableStickyHeader
          enableRowSelection={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
          enableSorting={false}
          enablePagination={false}
          initialState={{ 
            density: "compact"
          }}
          muiTableProps={{ 
            sx: { 
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                fontSize: '0.875rem',
              }
            } 
          }}
          muiTableHeadCellProps={{ 
            sx: { 
              backgroundColor: '#f8fafc',
              color: '#374151',
              fontWeight: 'bold',
              fontSize: '0.875rem'
            } 
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save Fields
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Profile Component
const ProfilePage = ({ entityType = 'customer', entityId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const config = PROFILE_CONFIGS[entityType];

  // State Management
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allProfiles, setAllProfiles] = useState([]);
  const [profileData, setProfileData] = useState({});
  const [originalProfileData, setOriginalProfileData] = useState({});
  const [contacts, setContacts] = useState([]);
  const [bankingData, setBankingData] = useState({});
  const [transactionData, setTransactionData] = useState([]);
  const [employmentData, setEmploymentData] = useState({});
  const [customFields, setCustomFields] = useState([]);
  const [customFieldBuilderOpen, setCustomFieldBuilderOpen] = useState(false);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
 const [statistics, setStatistics] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

 const [profile, setProfile] = useState(null);

  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    title: '',
    isPrimary: false,
    type: 'Main'
  });



  const [transactionDateRange, setTransactionDateRange] = useState({
    fromDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 year ago
    toDate: new Date().toISOString().split('T')[0] // Today
  });
  // Helper function to get custom fields for a specific tab
  const getCustomFieldsForTab = (tabId) => {
    return customFields.filter(field => field.targetTab === tabId);
  };
   const currentProfileId = entityId || originalProfileData?.id;
// In ProfilePage component, add this console.log
 useEffect(() => {
    console.log('ProfilePage Mounted:', {
      entityType,
      entityId,
      currentProfileId,
      originalProfileData
    });
  }, [entityType, entityId, currentProfileId]);
  // Load profile data on component mount
    useEffect(() => {
    if (entityId) {
      console.log('Loading profile by entityId:', entityId);
      loadProfileData();
    } else if (!entityId && activeTab === 'all') {
      // Only load all profiles if no specific entityId
      loadAllProfiles();
    }
  }, [entityType, entityId]);
  
  useEffect(() => {
  if (entityId) {
    loadProfileData();
  } else {
    // New profile - get next code
    fetchNextCode();
  }
}, [entityType, entityId]);

 useEffect(() => {
    if (activeTab === 'ledger' && currentProfileId) {
      console.log('Loading transactions for profile:', currentProfileId);
      loadTransactionData();
    }
  }, [activeTab, currentProfileId, transactionDateRange]);

   useEffect(() => {
    if (currentProfileId) {
      loadProfileStatistics();
    }
  }, [currentProfileId, entityType]);


   const loadTransactionData = async () => {
    if (!currentProfileId) {
      console.log('No profile ID for transactions');
      return;
    }

    setLoadingTransactions(true);
    try {
      console.log('API Call - Profile Transactions:', {
        profileId: currentProfileId,
        entityType,
        fromDate: transactionDateRange.fromDate,
        toDate: transactionDateRange.toDate
      });

      const result = await profileAPI.getProfileTransactions(
        currentProfileId,
        entityType,
        transactionDateRange.fromDate,
        transactionDateRange.toDate
      );
      
      console.log('Transactions Response:', result);
      
      if (result.success && result.data) {
        console.log('Transactions loaded:', result.data.length);
        setTransactionData(result.data);
      } else {
        showSnackbar(result.message || 'Failed to load transactions', 'error');
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
      showSnackbar('Error loading transactions', 'error');
    }
    setLoadingTransactions(false);
  };

  const loadProfileStatistics = async () => {
    if (!currentProfileId) {
      console.log('No profile ID for statistics');
      return;
    }

    try {
      console.log('API Call - Profile Statistics:', {
        profileId: currentProfileId,
        entityType
      });

      const result = await profileAPI.getProfileStatistics(currentProfileId, entityType);
      
      console.log('Statistics Response:', result);
      
      if (result.success && result.data) {
        setStatistics(result.data);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

const fetchNextCode = async () => {
  try {
    const result = await profileAPI.getNextCode(entityType);
    if (result.success && result.data) {
      setProfileData(prev => ({ ...prev, code: result.data }));
    }
  } catch (error) {
    console.error('Error fetching next code:', error);
  }
};
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const hideSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

 const loadAllProfiles = async () => {
  setLoading(true);
  try {
    const result = await profileAPI.getProfilesByType(
      entityType,
      1, // companyId
      {
        page: 1,
        pageSize: 100,
        search: '',
        sortBy: 'name',
        sortDirection: 'asc'
      }
    );
    
    if (result.success && result.data) {
      setAllProfiles(result.data);
    } else {
      showSnackbar(result.message || 'Failed to load profiles', 'error');
    }
  } catch (error) {
    console.error('Error loading profiles:', error);
    showSnackbar('Error loading profile list', 'error');
  }
  setLoading(false);
};
  // const loadProfileData = async () => {
  //   if (!currentProfileId) {
  //     console.log('No profile ID to load');
  //     return;
  //   }

  //   setLoading(true);
  //   try {
  //     console.log('API Call - Get Profile:', { id: currentProfileId, entityType });
      
  //     const result = await profileAPI.getProfile(currentProfileId, entityType);
      
  //     console.log('Profile Data Response:', result);
      
  //     if (result.success && result.data) {
  //       const profile = result.data;

  //       // Map API data to component state
  //       setProfileData({
  //         code: profile.code || '',
  //         name: profile.name || '',
  //         short_name: profile.shortName || '',
  //         type: profile.contactType || 'Company',
  //         status: profile.isActive ? 'Active' : 'Inactive',
  //         email: profile.primaryEmail || '',
  //         phone: profile.primaryPhone || '',
  //         country: profile.country || '',
  //         city: profile.city || '',
  //         address: profile.street || '',
  //         website: profile.website || '',
  //         since: profile.relationSince ? profile.relationSince.split('T')[0] : '',
  //         ntn: profile.taxNTN || '',
  //         strn: profile.taxSTRN || '',
  //         image: profile.profilePicture || `https://images.unsplash.com/photo-1560472354-b33ff0c44a43?q=80&w=512&auto=format&fit=max`
  //       });

  //       // Store original data
  //       setOriginalProfileData(profile);

  //       // Set banking data
  //       setBankingData({
  //         acct_title: profile.name || '',
  //         bank_name: profile.bankName || '',
  //         iban: profile.iban || profile.bankAccount || '',
  //         swift: profile.swiftCode || ''
  //       });

  //       // Set employment data for employees
  //       if (entityType === 'employee') {
  //         setEmploymentData({
  //           designation: profile.designationTitle || '',
  //           department: profile.departmentName || '',
  //           joining_date: profile.joinDate ? profile.joinDate.split('T')[0] : '',
  //           salary: profile.currentSalary || 0,
  //           employee_type: profile.employmentType || 'Full-time'
  //         });
  //       }

  //       // Set contacts
  //       setContacts([{
  //         name: profile.contactPerson || '',
  //         role: 'Primary Contact',
  //         email: profile.contactEmail || profile.primaryEmail || '',
  //         phone: profile.contactPhone || profile.primaryPhone || ''
  //       }]);

  //       // Set custom fields
  //       if (profile.customFields) {
  //         const customFieldsArray = Object.entries(profile.customFields).map(([key, value]) => ({
  //           label: key,
  //           type: value.type || 'text',
  //           options: value.options || '',
  //           value: value.value,
  //           targetTab: value.targetTab || 'info'
  //         }));
  //         setCustomFields(customFieldsArray);
  //       }

  //       // If we have entityId from prop, hide the "All Profiles" tab
  //       // by switching to 'info' tab automatically
  //       if (entityId) {
  //         setActiveTab('info');
  //       }

  //     } else {
  //       showSnackbar(result.message || 'Failed to load profile data', 'error');
  //     }
  //   } catch (error) {
  //     console.error('Error loading profile:', error);
  //     showSnackbar('Error loading profile data', 'error');
  //   }
  //   setLoading(false);
  // };

  const loadProfileData = async () => {
    try {
        if (!entityId) {
    console.log('No entityId provided for loading profile');
    return;
  }
      setLoading(true);
      const service = ServiceFactory.getService(entityType === 'customer' ? 'customers' : 'vendors');
      const response = await service.getById(entityId);

      if (response.success) {
        setProfile(response.data);
        setContacts(response.data.contacts || []);
        // Load profile image
        loadProfileImage();
      } else {
        setError(response.message || 'Failed to load profile');
      }

         const result = await profileAPI.getProfileById(entityId, entityType);
    
    console.log('Profile loaded:', result);
    
    if (result.success && result.data) {
      setProfileData(result.data);
      setOriginalProfileData(result.data);
      
      // Load custom fields if available
      if (result.data.customFields) {
        const fieldsArray = Object.entries(result.data.customFields).map(([key, value]) => ({
          key,
          ...value
        }));
        setCustomFields(fieldsArray);
      }
    } else {
      showSnackbar(result.message || 'Failed to load profile', 'error');
    }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  const saveProfileData = async (specificData = null) => {
    setSaving(true);
    try {
      // Merge all form data from different tabs into one complete object
      const apiData = specificData || {
        profileType: entityType.charAt(0).toUpperCase() + entityType.slice(1),
        code: profileData.code,
        name: profileData.name,
        shortName: profileData.short_name,
        contactType: profileData.type,
        primaryEmail: profileData.email,
        primaryPhone: profileData.phone,
        website: profileData.website,
        contactPerson: contacts[0]?.name || null,
        contactPhone: contacts[0]?.phone || null,
        contactEmail: contacts[0]?.email || null,
        street: profileData.address,
        city: profileData.city,
        state: null,
        country: profileData.country,
        postalCode: null,
        paymentTerms: entityType === 'customer' ? '15 Days' : '30 Days',
        preferredCurrency: 'PKR',
        category: null,
        relationSince: profileData.since || null,
        creditLimit: profileData.creditLimit || 0,
        // Banking data from banking tab
        bankName: bankingData.bank_name || null,
        bankAccount: bankingData.iban || null,
        iban: bankingData.iban || null,
        branchCode: null,
        swiftCode: bankingData.swift || null,
        // Tax data from tax tab
        taxNTN: profileData.ntn,
        taxSTRN: profileData.strn,
        taxRegistrationType: null,
        defaultTaxRate: profileData.defaultTaxRate || 0,
        // Employee specific fields from employment tab
        firstName: entityType === 'employee' ? profileData.name?.split(' ')[0] || null : null,
        lastName: entityType === 'employee' ? profileData.name?.split(' ').slice(1).join(' ') || null : null,
        fatherName: null, 
        dateOfBirth: null,
        gender: null, 
        maritalStatus: null,
        nationality: null,
        cnicNumber: null,
        employmentType: employmentData.employee_type || null,
        joinDate: employmentData.joining_date || null,
        departmentId: employmentData.departmentId || null,
        designationId: employmentData.designationId || null,
        currentSalary: employmentData.salary || 0,
        linkedAccountId: profileData.linkedAccountId || null,
        openingBalance: profileData.openingBalance || 0,
        notes: profileData.notes || null,
        // Custom fields from custom tab - now including targetTab info
        customFields: customFields.reduce((acc, field) => {
          if (field.label && field.value !== undefined) {
            acc[field.label] = {
              value: field.value || '',
              targetTab: field.targetTab || 'info',
              type: field.type || 'text',
              options: field.options || ''
            };
          }
          return acc;
        }, {})
    };
      let result;
      console.log("apiData",JSON.stringify(apiData))
      // Check if we have a profile ID (either from props or from loaded data)
      const profileId = originalProfileData.id || entityId;
      
      if (profileId) {
        // Update existing profile
        console.log("Updating profile with ID:", profileId);
        result = await profileAPI.updateProfile(profileId, entityType, apiData);
      } else {
        // Create new profile
        console.log("Creating new profile");
        result = await profileAPI.createProfile(apiData);
      }

      if (result.success) {
        showSnackbar('Profile saved successfully!', 'success');
        if (result.data?.id) {
          setOriginalProfileData(result.data);
        }
        // Refresh the all profiles list if on that tab
        if (activeTab === 'all') {
          loadAllProfiles();
        }
      } else {
        showSnackbar(result.message || 'Failed to save profile', 'error');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      showSnackbar('Error saving profile', 'error');
    }
    setSaving(false);
  };
  const loadProfileImage = async () => {
    try {
      const imageUrl = `${process.env.REACT_APP_API_URL}/api/FileUpload/${entityType}/${entityId}/profile-image`;
      setImagePreview(imageUrl);
    } catch (err) {
      console.log('No profile image found');
    }
  };
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileData({ ...profileData, image: e.target.result });
      };
      reader.readAsDataURL(file);
    }
  };
const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadProfileImage = async () => {
    if (!profileImage) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', profileImage);

      const endpoint = `${process.env.REACT_APP_API_URL}FileUpload/${entityType}/${entityId}/profile-image`;
      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result = await response.json();
      if (result.success) {
        setImagePreview(result.data);
        setProfileImage(null);
        alert('Profile image uploaded successfully!');
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

    const handleAddContact = () => {
      setEditingContact(null);
      setNewContact({
        name: '',
        email: '',
        phoneNumber: '',
        title: '',
        isPrimary: false,
        type: 'Main'
      });
      setOpenContactDialog(true);
    };

const handleEditContact = (contact) => {
    setEditingContact(contact.id);
    setNewContact(contact);
    setOpenContactDialog(true);
  };
 const handleSaveContact = async () => {
    try {
      // If setting as primary, unset other primary contacts
      if (newContact.isPrimary) {
        const updatedContacts = contacts.map(c => ({
          ...c,
          isPrimary: c.id === editingContact ? newContact.isPrimary : false
        }));
        setContacts(updatedContacts);
      }

      if (editingContact) {
        // Update existing contact
        const updatedContacts = contacts.map(c =>
          c.id === editingContact ? { ...newContact, id: editingContact } : c
        );
        setContacts(updatedContacts);
      } else {
        // Add new contact
        setContacts([...contacts, { ...newContact, id: Date.now() }]);
      }

      setOpenContactDialog(false);
      setNewContact({
        name: '',
        email: '',
        phoneNumber: '',
        title: '',
        isPrimary: false,
        type: 'Main'
      });
    } catch (err) {
      setError(err.message);
    }
  };
  const handleSaveAll = async () => {
    await saveProfileData();
  };
const handleDeleteContact = (contactId) => {
    setContacts(contacts.filter(c => c.id !== contactId));
  };

    const getPrimaryContact = () => {
    return contacts.find(c => c.isPrimary) || contacts[0];
  };
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  // Function to render custom fields in any tab
  const renderCustomFieldsInTab = (tabId) => {
    const tabCustomFields = getCustomFieldsForTab(tabId);
    
    if (tabCustomFields.length === 0) {
      return null;
    }

    return (
      <>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#111827', fontSize: '1rem' }}>
          Custom Fields
        </Typography>
        <Grid container spacing={1.5}>
          {tabCustomFields.map((field, index) => (
            <Grid item xs={12} sm={6} key={`${tabId}-${index}`}>
              {field.type === 'dropdown' ? (
                <FormControl 
                fullWidth 
                size="small"
                sx={{ 
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    width: '100%'
                  }
                }}
                >
                  <InputLabel>{field.label}</InputLabel>
                  <Select 
                    label={field.label}
                    value={field.value || ''}
                    onChange={(e) => {
                      const newFields = [...customFields];
                      const fieldIndex = newFields.findIndex(f => f.label === field.label && f.targetTab === tabId);
                      if (fieldIndex !== -1) {
                        newFields[fieldIndex].value = e.target.value;
                        setCustomFields(newFields);
                      }
                    }}
                    sx={{ width: '100%' }}
                  >
                    {field.options.split(',').map((option) => (
                      <MenuItem key={option.trim()} value={option.trim()}>
                        {option.trim()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  type={field.type === 'file' ? 'file' : field.type}
                  value={field.type !== 'file' ? field.value || '' : undefined}
                  onChange={(e) => {
                    const newFields = [...customFields];
                    const fieldIndex = newFields.findIndex(f => f.label === field.label && f.targetTab === tabId);
                    if (fieldIndex !== -1) {
                      newFields[fieldIndex].value = e.target.value;
                      setCustomFields(newFields);
                    }
                  }}
                  InputLabelProps={field.type === 'date' || field.type === 'file' ? { shrink: true } : undefined}
                />
              )}
            </Grid>
          ))}
        </Grid>
      </>
    );
  };

  // All Profiles table columns with inline editing
 const allProfilesColumns = [
    {
      accessorKey: 'code',
      header: 'Code',
      size: 150,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      size: 200,
    },
    {
      accessorKey: 'primaryEmail',
      header: 'Email',
      size: 220,
    },
    {
      accessorKey: 'primaryPhone',
      header: 'Phone',
      size: 150,
    },
    {
      accessorKey: 'city',
      header: 'City',
      size: 120,
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      size: 130,
      // We keep the Cell here to show a nice colored chip
      Cell: ({ row }) => (
        <Chip 
          label={row.original.isActive ? 'Active' : 'Inactive'} 
          color={row.original.isActive ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      accessorKey: 'actions',
      header: 'Action',
      size: 150,
      Cell: ({ row }) => (
        <Box display="flex" gap={1}>
            <Button

            size="small"

           

            color="primary"

            onClick={async () => {

              // Load this profile into the form for editing

              const profile = row.original;
                      console.log("profile", JSON.stringify(profile))

             

              // Map profile data to form state

              setProfileData({

                code: profile.code || '',

                name: profile.name || '',

                short_name: profile.shortName || '',

                type: profile.contactType || 'Company',

                status: profile.isActive ? 'Active' : 'Inactive',

                email: profile.primaryEmail || '',

                phone: profile.primaryPhone || '',

                country: profile.country || '',

                city: profile.city || '',

                address: profile.street || '',

                website: profile.website || '',

                since: profile.relationSince ? profile.relationSince.split('T')[0] : '',

                ntn: profile.taxNTN || '',

                strn: profile.taxSTRN || '',

                image: profile.profilePicture || `https://images.unsplash.com/photo-1560472354-b33ff0c44a43?q=80&w=512&auto=format&fit=max`

              });



              setOriginalProfileData(profile);



              // Set banking data

              setBankingData({

                acct_title: profile.name || '',

                bank_name: profile.bankName || '',

                iban: profile.iban || profile.bankAccount || '',

                swift: profile.swiftCode || ''

              });



              // Set employment data for employees

              if (entityType === 'employee') {

                setEmploymentData({

                  designation: profile.designationTitle || '',

                  department: profile.departmentName || '',

                  joining_date: profile.joinDate ? profile.joinDate.split('T')[0] : '',

                  salary: profile.currentSalary || 0,

                  employee_type: profile.employmentType || 'Full-time'

                });

              }



              // Set contacts

              setContacts([

                {

                  name: profile.contactPerson || '',

                  role: 'Primary Contact',

                  email: profile.contactEmail || profile.primaryEmail || '',

                  phone: profile.contactPhone || profile.primaryPhone || ''

                }

              ]);



              // Set custom fields

              if (profile.customFields) {

                const customFieldsArray = Object.entries(profile.customFields).map(([key, value]) => ({

                  label: key,

                  type: value.type,

                  options:value.options,

                  value: value.value,
                  targetTab: value.targetTab

                }));
                  console.log("customFieldsArray",customFieldsArray)
                setCustomFields(customFieldsArray);

              }



              // Switch to info tab to show the loaded data

              setActiveTab('info');

              showSnackbar('Profile loaded for editing', 'info');

            }}

          >

            Edit

          </Button>
          {/* <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDelete(row.original)} // Assumes you have an handleDelete function
          >
            Delete
          </Button> */}
        </Box>
      )
    }
];

  const renderAllProfiles = () => (
    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #f1f5f9' }}>
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.125rem' }}>
            All {config.entityName}s
          </Typography>
          {/* <Button 
            variant="contained"
            startIcon={<Add />}
            size="small"
            onClick={() => {
              setAllProfiles([...allProfiles, {
                code: '',
                name: '',
                primaryEmail: '',
                primaryPhone: '',
                city: '',
                isActive: true
              }]);
            }}
            sx={{ 
              bgcolor: '#800000',
              '&:hover': { bgcolor: '#600000' }
            }}
          >
            Add New
          </Button> */}
        </Box>
        <Box sx={{ overflow: 'auto' }}>
          <MaterialReactTable
            columns={allProfilesColumns}
            data={allProfiles}
            enableColumnOrdering
            enableColumnResizing
            enableStickyHeader
            enableRowSelection={false}
            enableColumnFilters
            enableGlobalFilter
            enableSorting
            enablePagination
            initialState={{ 
              density: "compact", 
              showColumnFilters: false,
              showGlobalFilter: true,
              pagination: { pageSize: 25, pageIndex: 0 }
            }}
            muiTableProps={{ 
              sx: { 
                minWidth: 800,
                '& .MuiTableCell-root': {
                  fontSize: '0.8rem',
                  py: 0.5,
                  px: 1
                }
              } 
            }}
            muiTableHeadCellProps={{ 
              sx: { 
                backgroundColor: '#f8fafc',
                color: '#374151',
                fontWeight: 'bold',
                fontSize: '0.8rem',
                py: 1
              } 
            }}
            muiSearchTextFieldProps={{
              size: 'small',
              variant: 'outlined',
            }}
            muiPaginationProps={{
              size: 'small',
              rowsPerPageOptions: [10, 25, 50, 100],
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );

  // Contact table columns
  const contactColumns = [
    {
      accessorKey: 'name',
      header: 'Full Name',
      size: 200,
      Cell: ({ row }) => (
        <TextField
          fullWidth
          size="small"
          value={row.original.name}
          onChange={(e) => {
            const newContacts = [...contacts];
            newContacts[row.index].name = e.target.value;
            setContacts(newContacts);
          }}
          placeholder="Enter name"
        />
      )
    },
    {
      accessorKey: 'role',
      header: 'Position/Role',
      size: 180,
      Cell: ({ row }) => (
        <TextField
          fullWidth
          size="small"
          value={row.original.role}
          onChange={(e) => {
            const newContacts = [...contacts];
            newContacts[row.index].role = e.target.value;
            setContacts(newContacts);
          }}
          placeholder="Enter role"
        />
      )
    },
    {
      accessorKey: 'email',
      header: 'Email Address',
      size: 220,
      Cell: ({ row }) => (
        <TextField
          fullWidth
          size="small"
          type="email"
          value={row.original.email}
          onChange={(e) => {
            const newContacts = [...contacts];
            newContacts[row.index].email = e.target.value;
            setContacts(newContacts);
          }}
          placeholder="Enter email"
        />
      )
    },
    {
      accessorKey: 'phone',
      header: 'Phone Number',
      size: 160,
      Cell: ({ row }) => (
        <TextField
          fullWidth
          size="small"
          value={row.original.phone}
          onChange={(e) => {
            const newContacts = [...contacts];
            newContacts[row.index].phone = e.target.value;
            setContacts(newContacts);
          }}
          placeholder="Enter phone"
        />
      )
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 100,
      Cell: ({ row }) => (
        <Button
          onClick={() => {
            const newContacts = contacts.filter((_, i) => i !== row.index);
            setContacts(newContacts);
          }}
          color="error"
          size="small"
          variant="outlined"
        >
          Delete
        </Button>
      )
    }
  ];
const fmtMoney = (n) => {
    if (n === null || n === undefined) return "PKR 0.00";
    return new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' }).format(n);
  };

  // Transaction table columns
  const transactionColumns = [
    { 
      accessorKey: 'date', 
      header: 'Date', 
      size: 120,
      Cell: ({ cell }) => new Date(cell.getValue()).toLocaleDateString()
    },
    { 
      accessorKey: 'documentNumber', 
      header: 'Document #', 
      size: 130 
    },
    { 
      accessorKey: 'documentType', 
      header: 'Type', 
      size: 150,
      Cell: ({ cell }) => (
        <Chip 
          label={cell.getValue()} 
          size="small"
          color={
            cell.getValue().includes('Bill') || cell.getValue().includes('Invoice') ? 'primary' :
            cell.getValue().includes('Payment') || cell.getValue().includes('Receipt') ? 'success' :
            'default'
          }
        />
      )
    },
    { 
      accessorKey: 'description', 
      header: 'Description', 
      size: 250 
    },
    { 
      accessorKey: 'debit', 
      header: 'Debit',
      size: 120,
      muiTableHeadCellProps: { align: 'right' },
      muiTableBodyCellProps: { align: 'right' },
      Cell: ({ cell }) => cell.getValue() ? fmtMoney(cell.getValue()) : ''
    },
    { 
      accessorKey: 'credit', 
      header: 'Credit',
      size: 120,
      muiTableHeadCellProps: { align: 'right' },
      muiTableBodyCellProps: { align: 'right' },
      Cell: ({ cell }) => cell.getValue() ? fmtMoney(cell.getValue()) : ''
    },
    { 
      accessorKey: 'balance', 
      header: 'Balance',
      size: 120,
      muiTableHeadCellProps: { align: 'right' },
      muiTableBodyCellProps: { align: 'right' },
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return (
          <Typography
            sx={{ 
              color: value >= 0 ? '#059669' : '#dc2626',
              fontWeight: 600
            }}
          >
            {fmtMoney(Math.abs(value))}
          </Typography>
        );
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      Cell: ({ cell }) => (
        <Chip 
          label={cell.getValue()} 
          size="small"
          color={
            cell.getValue() === 'Posted' ? 'success' :
            cell.getValue() === 'Draft' ? 'default' :
            'warning'
          }
        />
      )
    },
    {
      accessorKey: 'journalEntryNumber',
      header: 'Journal Entry',
      size: 130,
      Cell: ({ cell }) => cell.getValue() || '-'
    }
  ];
  const renderInfoForm = () => (
    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #f1f5f9' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#111827', fontSize: '1.125rem' }}>
          {entityType === 'employee' ? 'Personal Information' : 'Profile Information'}
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label={entityType === 'employee' ? "Employee Code *" : "Profile Code *"}
              value={profileData.code || ''}
              onChange={(e) => setProfileData({ ...profileData, code: e.target.value })}
              required
                InputProps={{
              readOnly: true, // ← Make it read-only
            }}
             sx={{
              '& .MuiInputBase-input': {
                backgroundColor: '#f8fafc', // Visual indicator it's read-only
                cursor: 'not-allowed'
              }
            }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={8}>
            <TextField
              fullWidth
              size="small"
              label="Full Name / Legal Name *"
              value={profileData.name || ''}
              onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Short Name"
              value={profileData.short_name || ''}
              onChange={(e) => setProfileData({ ...profileData, short_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={profileData.type || 'Company'}
                label="Type"
                onChange={(e) => setProfileData({ ...profileData, type: e.target.value })}
              >
                <MenuItem value="Company">Company</MenuItem>
                <MenuItem value="Individual">Individual</MenuItem>
                <MenuItem value="Partnership">Partnership</MenuItem>
                <MenuItem value="Non-profit">Non-profit</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status *</InputLabel>
              <Select
                value={profileData.status || 'Active'}
                label="Status *"
                onChange={(e) => setProfileData({ ...profileData, status: e.target.value })}
                required
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
                <MenuItem value="Suspended">Suspended</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Email Address *"
              type="email"
              value={profileData.email || ''}
              onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Phone Number *"
              value={profileData.phone || ''}
              onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Country *</InputLabel>
              <Select
                value={profileData.country || 'United States'}
                label="Country *"
                onChange={(e) => setProfileData({ ...profileData, country: e.target.value })}
                required
              >
                <MenuItem value="United States">United States</MenuItem>
                <MenuItem value="Canada">Canada</MenuItem>
                <MenuItem value="United Kingdom">United Kingdom</MenuItem>
                <MenuItem value="Pakistan">Pakistan</MenuItem>
                <MenuItem value="UAE">UAE</MenuItem>
                <MenuItem value="India">India</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="City *"
              value={profileData.city || ''}
              onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Full Address"
              multiline
              rows={3}
              value={profileData.address || ''}
              onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Website"
              value={profileData.website || ''}
              onChange={(e) => setProfileData({ ...profileData, website: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Member Since"
              type="date"
              value={profileData.since || ''}
              onChange={(e) => setProfileData({ ...profileData, since: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {/* Render custom fields for info tab */}
        {renderCustomFieldsInTab('info')}
        
        <Box mt={2} display="flex" gap={1.5} flexWrap="wrap">
          <Button 
            variant="contained" 
            startIcon={<Save />}
            onClick={() => saveProfileData()}
            disabled={saving}
            sx={{ 
              bgcolor: '#800000',
              '&:hover': { bgcolor: '#600000' },
              minWidth: 120,
              py: 0.75,
              fontSize: '0.8rem'
            }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Profile'}
          </Button>
          <Button variant="outlined" onClick={loadProfileData} disabled={loading} sx={{ py: 0.75, fontSize: '0.8rem' }}>
            Reset Form
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderContacts = () => (
    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #f1f5f9' }}>
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.125rem' }}>
            Contact Information
          </Typography>
          <Button 
            onClick={() => setContacts([...contacts, { name: '', role: '', email: '', phone: '' }])} 
            variant="contained"
            size="small"
            startIcon={<Add />}
            sx={{ 
              bgcolor: '#800000',
              '&:hover': { bgcolor: '#600000' },
              py: 0.75,
              fontSize: '0.8rem'
            }}
          >
            Add Contact
          </Button>
        </Box>
        <Box sx={{ overflow: 'auto' }}>
          <MaterialReactTable
            columns={contactColumns}
            data={contacts}
            enableColumnOrdering
            enableColumnResizing
            enableStickyHeader
            enableRowSelection={false}
            enableColumnFilters
            enableGlobalFilter
            enableSorting
            enablePagination
            initialState={{ 
              density: "compact", 
              showColumnFilters: false,
              showGlobalFilter: true,
              pagination: { pageSize: 25, pageIndex: 0 }
            }}
            muiTableProps={{ 
              sx: { 
                minWidth: 800,
                '& .MuiTableCell-root': {
                  fontSize: '0.8rem',
                  py: 0.5,
                  px: 1
                }
              } 
            }}
            muiTableHeadCellProps={{ 
              sx: { 
                backgroundColor: '#f8fafc',
                color: '#374151',
                fontWeight: 'bold',
                fontSize: '0.8rem',
                py: 1
              } 
            }}
            muiTableBodyCellProps={{ 
              sx: { 
                fontSize: '0.8rem',
              } 
            }}
            muiSearchTextFieldProps={{
              size: 'small',
              variant: 'outlined',
            }}
            muiPaginationProps={{
              size: 'small',
              rowsPerPageOptions: [10, 25, 50, 100],
            }}
          />
        </Box>
        
        {/* Render custom fields for contacts tab */}
        {renderCustomFieldsInTab('contacts')}
        
        <Box mt={2} display="flex" gap={1.5} flexWrap="wrap">
          <Button 
            variant="contained" 
            startIcon={<Save />}
            onClick={() => saveProfileData()}
            disabled={saving}
            sx={{ 
              bgcolor: '#800000',
              '&:hover': { bgcolor: '#600000' },
              minWidth: 120,
              py: 0.75,
              fontSize: '0.8rem'
            }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Contacts'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderBanking = () => (
    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #f1f5f9' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#111827', fontSize: '1.125rem' }}>
          Banking Information
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Account Title *"
              value={bankingData.acct_title || ''}
              onChange={(e) => setBankingData({ ...bankingData, acct_title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Bank Name *"
              value={bankingData.bank_name || ''}
              onChange={(e) => setBankingData({ ...bankingData, bank_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="IBAN / Account Number *"
              value={bankingData.iban || ''}
              onChange={(e) => setBankingData({ ...bankingData, iban: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="SWIFT / Routing Code"
              value={bankingData.swift || ''}
              onChange={(e) => setBankingData({ ...bankingData, swift: e.target.value })}
            />
          </Grid>
        </Grid>
        
        {/* Render custom fields for banking tab */}
        {renderCustomFieldsInTab('banking')}
        
        <Box mt={2} display="flex" gap={1.5} flexWrap="wrap">
          <Button 
            variant="contained" 
            startIcon={<Save />}
            onClick={() => saveProfileData()}
            disabled={saving}
            sx={{ 
              bgcolor: '#800000',
              '&:hover': { bgcolor: '#600000' },
              minWidth: 120,
              py: 0.75,
              fontSize: '0.8rem'
            }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Banking'}
          </Button>
          <Button variant="outlined" onClick={loadProfileData} sx={{ py: 0.75, fontSize: '0.8rem' }}>Reset</Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderEmploymentDetails = () => (
    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #f1f5f9' }}>
        <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#111827', fontSize: '1.125rem' }}>
                Employment Details
            </Typography>
            <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Designation / Job Title"
                        value={employmentData.designation || ''}
                        onChange={(e) => setEmploymentData({ ...employmentData, designation: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Department"
                        value={employmentData.department || ''}
                        onChange={(e) => setEmploymentData({ ...employmentData, department: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Joining Date"
                        type="date"
                        value={employmentData.joining_date || ''}
                        onChange={(e) => setEmploymentData({ ...employmentData, joining_date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Employee Type</InputLabel>
                      <Select
                          value={employmentData.employee_type || 'Full-time'}
                          label="Employee Type"
                          onChange={(e) => setEmploymentData({ ...employmentData, employee_type: e.target.value })}
                      >
                          <MenuItem value="Full-time">Full-time</MenuItem>
                          <MenuItem value="Part-time">Part-time</MenuItem>
                          <MenuItem value="Contractor">Contractor</MenuItem>
                          <MenuItem value="Intern">Intern</MenuItem>
                      </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Salary (USD)"
                        type="number"
                        value={employmentData.salary || ''}
                        onChange={(e) => setEmploymentData({ ...employmentData, salary: e.target.value })}
                    />
                </Grid>
            </Grid>
            
            {/* Render custom fields for employment tab */}
            {renderCustomFieldsInTab('employment')}
            
            <Box mt={2} display="flex" gap={1.5} flexWrap="wrap">
              <Button 
                variant="contained" 
                startIcon={<Save />}
                onClick={() => saveProfileData()}
                disabled={saving}
                sx={{ 
                  bgcolor: '#800000',
                  '&:hover': { bgcolor: '#600000' },
                  minWidth: 120,
                  py: 0.75,
                  fontSize: '0.8rem'
                }}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Employment'}
              </Button>
              <Button variant="outlined" onClick={loadProfileData} sx={{ py: 0.75, fontSize: '0.8rem' }}>Reset</Button>
            </Box>
        </CardContent>
    </Card>
  );

  const renderTransactions = () => (
    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #f1f5f9' }}>
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.125rem' }}>
              {entityType === 'employee' ? 'Payroll History' : 'Transaction Records'}
            </Typography>
            {statistics && (
              <Box display="flex" gap={2} mt={1}>
                <Chip 
                  label={`Balance: ${fmtMoney(statistics.currentBalance)}`}
                  color={statistics.currentBalance >= 0 ? 'success' : 'error'}
                  size="small"
                />
                {entityType === 'vendor' && (
                  <>
                    <Chip 
                      label={`Unpaid Bills: ${statistics.unpaidBills}`}
                      color={statistics.unpaidBills > 0 ? 'warning' : 'default'}
                      size="small"
                    />
                    <Chip 
                      label={`Outstanding: ${fmtMoney(statistics.unpaidAmount)}`}
                      color={statistics.unpaidAmount > 0 ? 'warning' : 'default'}
                      size="small"
                    />
                  </>
                )}
                {entityType === 'customer' && (
                  <>
                    <Chip 
                      label={`Unpaid Invoices: ${statistics.unpaidInvoices}`}
                      color={statistics.unpaidInvoices > 0 ? 'warning' : 'default'}
                      size="small"
                    />
                    <Chip 
                      label={`Outstanding: ${fmtMoney(statistics.unpaidAmount)}`}
                      color={statistics.unpaidAmount > 0 ? 'warning' : 'default'}
                      size="small"
                    />
                  </>
                )}
              </Box>
            )}
          </Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              type="date"
              label="From"
              value={transactionDateRange.fromDate}
              onChange={(e) => setTransactionDateRange(prev => ({ ...prev, fromDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 140 }}
            />
            <Typography variant="body2" sx={{ color: '#6b7280', mx: 1 }}>to</Typography>
            <TextField
              size="small"
              type="date"
              label="To"
              value={transactionDateRange.toDate}
              onChange={(e) => setTransactionDateRange(prev => ({ ...prev, toDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 140 }}
            />
            <Button 
              variant="outlined" 
              size="small"
              onClick={loadTransactionData}
              disabled={loadingTransactions}
            >
              {loadingTransactions ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
          </Box>
        </Box>

        {loadingTransactions ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ overflow: 'auto' }}>
            <MaterialReactTable
              columns={transactionColumns}
              data={transactionData}
              enableColumnOrdering
              enableColumnResizing
              enableStickyHeader
              enableRowSelection={false}
              enableColumnFilters
              enableGlobalFilter
              enableSorting
              enablePagination
              initialState={{ 
                density: "compact", 
                showColumnFilters: false,
                showGlobalFilter: true,
                pagination: { pageSize: 25, pageIndex: 0 }
              }}
              muiTableProps={{ 
                sx: { 
                  minWidth: 800,
                  '& .MuiTableCell-root': {
                    fontSize: '0.8rem',
                    py: 0.5,
                    px: 1
                  }
                } 
              }}
              muiTableHeadCellProps={{ 
                sx: { 
                  backgroundColor: '#f8fafc',
                  color: '#374151',
                  fontWeight: 'bold',
                  fontSize: '0.8rem',
                  py: 1
                } 
              }}
              muiTableBodyCellProps={{ 
                sx: { 
                  fontSize: '0.8rem',
                } 
              }}
              muiSearchTextFieldProps={{
                size: 'small',
                variant: 'outlined',
              }}
              muiPaginationProps={{
                size: 'small',
                rowsPerPageOptions: [10, 25, 50, 100],
              }}
            />
          </Box>
        )}
        
        {/* Render custom fields for transactions/payroll tab */}
        {renderCustomFieldsInTab(entityType === 'employee' ? 'payroll' : 'ledger')}
      </CardContent>
    </Card>
  );


  const renderCustomFields = () => (
    <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #f1f5f9' }}>
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.125rem' }}>
            Custom Fields Management
          </Typography>
          <Button 
            onClick={() => setCustomFieldBuilderOpen(true)} 
            variant="contained"
            size="small"
            startIcon={<Add />}
            sx={{ 
              bgcolor: '#800000',
              '&:hover': { bgcolor: '#600000' }
            }}
          >
            Add New Field
          </Button>
        </Box>

        {customFields.length > 0 && (
          <Box mb={3}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827', mb: 1.5, fontSize: '1rem' }}>
              Active Custom Fields ({customFields.length})
            </Typography>
            <Grid container spacing={1.5}>
              {customFields.map((field, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                        {field.label}
                      </Typography>
                      <Box display="flex" gap={0.5}>
                        <Chip 
                          label={field.type} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                        <Chip 
                          label={config.tabs.concat(config.moreTabs).find(tab => tab.id === field.targetTab)?.label || field.targetTab} 
                          size="small" 
                          color="secondary" 
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    {field.type === 'dropdown' && field.options && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Options: {field.options}
                      </Typography>
                    )}
                    <Box mt={1} display="flex" gap={1}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        color="error"
                        onClick={() => {
                          const newFields = customFields.filter((_, i) => i !== index);
                          setCustomFields(newFields);
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" gap={1.5}>
              <Button 
                variant="contained" 
                startIcon={<Save />}
                onClick={() => saveProfileData()}
                disabled={saving}
                sx={{ 
                  bgcolor: '#800000',
                  '&:hover': { bgcolor: '#600000' },
                  minWidth: 120,
                  py: 0.75,
                  fontSize: '0.8rem'
                }}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Custom Fields'}
              </Button>
            </Box>
          </Box>
        )}

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827', mb: 1.5, fontSize: '1rem' }}>
            Field Builder
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
            Create custom fields to capture additional information specific to your needs. Select which tab each field should appear in.
          </Typography>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Created fields will appear in their selected tab and can be managed here.
          </Alert>
          {customFields.length === 0 && (
            <Box textAlign="center" py={3}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
                No custom fields have been created yet.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click "Add New Field" to get started.
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'all': return renderAllProfiles();
      case 'info': return renderInfoForm();
      case 'contacts': return renderContacts();
      case 'banking': return renderBanking();
      case 'employment': return renderEmploymentDetails();
      case 'payroll': return renderTransactions();
      case 'tax': return (
        <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #f1f5f9' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#111827', fontSize: '1.125rem' }}>
              Tax & Compliance
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={entityType === 'employee' ? 'Social Security Number' : 'Tax ID Number'}
                  value={profileData.ntn || ''}
                  onChange={(e) => setProfileData({ ...profileData, ntn: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={entityType === 'employee' ? 'Tax Filing Status' : 'Secondary Tax Number'}
                  value={profileData.strn || ''}
                  onChange={(e) => setProfileData({ ...profileData, strn: e.target.value })}
                />
              </Grid>
              {entityType === 'employee' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="W-4 Allowances"
                      type="number"
                      placeholder="0"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tax Withholding</InputLabel>
                      <Select defaultValue="standard">
                        <MenuItem value="standard">Standard</MenuItem>
                        <MenuItem value="additional">Additional Amount</MenuItem>
                        <MenuItem value="exempt">Exempt</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>
            
            {/* Render custom fields for tax tab */}
            {renderCustomFieldsInTab('tax')}
            
            <Box mt={2} display="flex" gap={1.5} flexWrap="wrap">
              <Button 
                variant="contained" 
                startIcon={<Save />}
                onClick={() => saveProfileData()}
                disabled={saving}
                sx={{ 
                  bgcolor: '#800000',
                  '&:hover': { bgcolor: '#600000' },
                  minWidth: 120,
                  py: 0.75,
                  fontSize: '0.8rem'
                }}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Tax Info'}
              </Button>
              <Button variant="outlined" onClick={loadProfileData} sx={{ py: 0.75, fontSize: '0.8rem' }}>Reset</Button>
            </Box>
          </CardContent>
        </Card>
      );
      case 'documents': return (
        <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #f1f5f9' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#111827', fontSize: '1.125rem' }}>
              Documents
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {entityType === 'employee' ? 'Upload Resume/CV' : 'Upload Tax Certificate'}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="file"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {entityType === 'employee' ? 'Upload ID Copy' : 'Upload Bank Letter'}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="file"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              {entityType === 'employee' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Upload Educational Certificates
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="file"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ multiple: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Upload Contract/Offer Letter
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="file"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
            
            {/* Render custom fields for documents tab */}
            {renderCustomFieldsInTab('documents')}
            
            <Box mt={2} display="flex" gap={1.5} flexWrap="wrap">
              <Button 
                variant="contained" 
                startIcon={<Save />}
                onClick={() => saveProfileData()}
                disabled={saving}
                sx={{ 
                  bgcolor: '#800000',
                  '&:hover': { bgcolor: '#600000' },
                  minWidth: 120,
                  py: 0.75,
                  fontSize: '0.8rem'
                }}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Documents'}
              </Button>
              <Button variant="outlined" onClick={loadProfileData} sx={{ py: 0.75, fontSize: '0.8rem' }}>Reset</Button>
            </Box>
          </CardContent>
        </Card>
      );
      case 'ledger': return renderTransactions();
      case 'custom': return renderCustomFields();
      case 'addresses': return (
        <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #f1f5f9' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#111827', fontSize: '1.125rem' }}>
              Address Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {entityType === 'employee' 
                ? 'Manage home address and emergency contact addresses here.' 
                : 'Manage billing and shipping addresses here.'}
            </Typography>
            
            {/* Render custom fields for addresses tab */}
            {renderCustomFieldsInTab('addresses')}
          </CardContent>
        </Card>
      );
      case 'notes': return (
        <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #f1f5f9' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#111827', fontSize: '1.125rem' }}>
              {entityType === 'employee' ? 'Performance Notes' : 'Internal Notes'}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={8}
              label={entityType === 'employee' ? 'Performance & Development Notes' : 'Notes'}
              placeholder={entityType === 'employee' 
                ? 'Add performance reviews, development goals, feedback, etc...' 
                : 'Add internal notes or comments here...'}
              variant="outlined"
            />
            
            {/* Render custom fields for notes tab */}
            {renderCustomFieldsInTab('notes')}
            
            <Box mt={2} display="flex" gap={1.5} flexWrap="wrap">
              <Button 
                variant="contained" 
                startIcon={<Save />}
                onClick={() => saveProfileData()}
                disabled={saving}
                sx={{ 
                  bgcolor: '#800000',
                  '&:hover': { bgcolor: '#600000' },
                  minWidth: 120,
                  py: 0.75,
                  fontSize: '0.8rem'
                }}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Notes'}
              </Button>
              <Button variant="outlined" sx={{ py: 0.75, fontSize: '0.8rem' }}>Clear</Button>
            </Box>
          </CardContent>
        </Card>
      );
      case 'history': return (
        <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #f1f5f9' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#111827', fontSize: '1.125rem' }}>
              Change History
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track all changes and modifications to this {config.entityName.toLowerCase()} profile.
            </Typography>
            
            {/* Render custom fields for history tab */}
            {renderCustomFieldsInTab('history')}
          </CardContent>
        </Card>
      );
      default: return renderInfoForm();
    }
  };

  if (loading) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6">Loading Profile...</Typography>
        </Box>
      </Backdrop>
    );
  }

   if (!profile) {
      return (
        <Alert severity="error">
          Profile not found
        </Alert>
      );
    }

      const primaryContact = getPrimaryContact();


const visibleTabs = entityId 
    ? config.tabs.filter(tab => tab.id !== 'all') // Remove "All Profiles" tab
    : config.tabs;
  return (
    <Box sx={{ 
      p: { xs: 1.5, md: 2 }, 
      bgcolor: '#f8fafc', 
      minHeight: '100vh',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <Box maxWidth="1400px" mx="auto">
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800,
              color: '#111827',
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.875rem' }
            }}
          >
            {config.entityName} Profile
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {/* ======================= NEW AI BUTTON WITH BLINKING IMAGE ======================= */}
  {['vendor', 'customer', 'employee'].includes(config.entityName.toLowerCase()) && (
    <Button 
      variant="outlined" 
      size="small"
      // Add your onClick function for the AI agent here
      // onClick={handleAiAgentClick} 
      sx={{ 
        border : 'none',
        color: '#374151',
        '&:hover': {
          borderColor: '#800000',
          color: '#800000',
          backgroundColor: 'rgba(128, 0, 0, 0.04)'
        },
        py: 0.5,
        px: 1.5,
        fontSize: '0.8rem',
        textTransform: 'none', // Keeps the text case as you wrote it
        minWidth: { xs: 'auto', sm: 100 }
      }}
    >
      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* This part handles the text logic */}
        <span style={{fontWeight:'bold'}}>
          {(() => {
            switch (config.entityName.toLowerCase()) {
              case 'vendor':
                return 'AI Accountant';
              case 'customer':
                return 'AI Sales Agent';
              case 'employee':
                return 'AI HR Agent';
              default:
                return '';
            }
          })()}
        </span>
        
        {/* This is your blinking IMAGE */}
        <Box
          component="img"
          
          src="/image/icon-ai.jpg" 
          alt="AI Assistant"
          sx={{ 
            height: '50px', // Icon-like size
            width: '40px',  // Icon-like size
            top : '-10px',
            '@keyframes blinker': {
              '50%': { opacity: 0.3 },
            },
            animation: 'blinker 1.5s linear infinite',
          }} 
        />
      </Box>
    </Button>
  )}
            <Button 
              variant="outlined" 
              startIcon={<Print />} 
              onClick={() => window.print()}
              size="small"
              sx={{ 
                borderColor: '#e5e7eb',
                color: '#374151',
                '&:hover': {
                  borderColor: '#800000',
                  color: '#800000'
                },
                py: 0.5,
                px: 1.5,
                fontSize: '0.8rem',
                minWidth: { xs: 'auto', sm: 100 }
              }}
            >
              {isMobile ? 'Print' : 'Print'}
            </Button>
            <Button 
              variant="contained" 
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />} 
              onClick={handleSaveAll}
              disabled={saving}
              size="small"
              sx={{ 
                bgcolor: '#800000',
                '&:hover': { bgcolor: '#600000' },
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                py: 0.5,
                px: 1.5,
                fontSize: '0.8rem',
                minWidth: { xs: 'auto', sm: 120 }
              }}
            >
              {saving ? 'Saving...' : isMobile ? 'Save' : 'Save All'}
            </Button>
          </Box>
        </Box>

        {/* Enhanced Tab Navigation */}
        <Card sx={{ 
          mb: 2,
          borderRadius: 2, 
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <Box sx={{ p: 1 }}>
            <Box 
              display="flex" 
              alignItems="center" 
              gap={0.5}
              sx={{ 
                width: '100%',
                minHeight: 36
              }}
            >
              {visibleTabs.slice(0, isMobile ? 3 : isTablet ? 4 : 5).map((tab) => (
                <Button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  size="small"
                  sx={{
                    px: { xs: 1, sm: 1.5 },
                    py: 0.5,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    fontWeight: activeTab === tab.id ? 700 : 500,
                    color: activeTab === tab.id ? 'white' : '#374151',
                    backgroundColor: activeTab === tab.id ? '#800000' : 'transparent',
                    borderRadius: 1.5,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    flex: { xs: '1 1 auto', sm: '0 0 auto' },
                    maxWidth: { xs: 110, sm: 'none' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '&:hover': {
                      backgroundColor: activeTab === tab.id ? '#800000' : '#f3f4f6',
                      color: activeTab === tab.id ? 'white' : '#111827',
                      transform: 'translateY(-1px)',
                      boxShadow: activeTab === tab.id ? '0 4px 8px rgba(128, 0, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  {isMobile && tab.label.length > 8 ? 
                    tab.label.split(' ')[0] : 
                    tab.label
                  }
                </Button>
              ))}
              
              <Box>
                <Button
                  onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                  size="small"
                  endIcon={<KeyboardArrowDown />}
                  sx={{
                    px: { xs: 1, sm: 1.5 },
                    py: 0.5,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    fontWeight: 500,
                    color: '#374151',
                    borderRadius: 1.5,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: '#f3f4f6',
                      color: '#111827',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  More
                </Button>
                <Menu
                  anchorEl={moreMenuAnchor}
                  open={Boolean(moreMenuAnchor)}
                  onClose={() => setMoreMenuAnchor(null)}
                  PaperProps={{
                    sx: {
                      borderRadius: 2,
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      minWidth: 180,
                      mt: 1
                    }
                  }}
                >
                  {visibleTabs.slice(isMobile ? 3 : isTablet ? 4 : 5).map((tab) => (
                    <MenuItem
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setMoreMenuAnchor(null);
                      }}
                      sx={{
                        py: 1,
                        px: 1.5,
                        fontSize: '0.8rem',
                        '&:hover': {
                          backgroundColor: '#f3f4f6'
                        }
                      }}
                    >
                      {tab.label}
                    </MenuItem>
                  ))}
                  
                  {(visibleTabs.slice(isMobile ? 3 : isTablet ? 4 : 5).length > 0 && config.moreTabs.length > 0) && (
                    <Divider sx={{ my: 0.5 }} />
                  )}
                  
                  {config.moreTabs.map((tab) => (
                    <MenuItem
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setMoreMenuAnchor(null);
                      }}
                      sx={{
                        py: 1,
                        px: 1.5,
                        fontSize: '0.8rem',
                        '&:hover': {
                          backgroundColor: '#f3f4f6'
                        }
                      }}
                    >
                      {tab.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Box>
          </Box>
        </Card>

        {/* Main Content Layout */}
        <Box 
          display="grid" 
          gridTemplateColumns={{ 
            xs: '1fr', 
            lg: activeTab === 'all' ? '1fr' : '280px 1fr' 
          }} 
          gap={2}
        >
          {/* Left Side - Profile Summary (hide on All Profiles tab) */}
          {activeTab !== 'all' && (
          <Card sx={{ 
            height: 'fit-content', 
            borderRadius: 2, 
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #f1f5f9',
            order: { xs: 2, lg: 1 }
          }}>
            <CardContent sx={{ p: 2, textAlign: 'center' }}>
              <Box position="relative" display="inline-block" mb={1.5}>
                <Avatar
                  src={profileData.image}
                  sx={{ 
                    width: { xs: 90, md: 100 }, 
                    height: { xs: 90, md: 100 }, 
                    mx: 'auto',
                    border: '3px solid #f3f4f6',
                    borderRadius: 2,
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {profileData.name?.charAt(0)}
                </Avatar>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="image-upload"
                  onChange={handleImageChange}
                />
                <label htmlFor="image-upload">
                  <IconButton
                    component="span"
                    size="small"
                    sx={{ 
                      position: 'absolute', 
                      bottom: 0, 
                      right: 0,
                      bgcolor: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: 1.5,
                      width: 28,
                      height: 28,
                      '&:hover': {
                        bgcolor: '#800000',
                        color: 'white',
                        borderColor: '#800000'
                      }
                    }}
                  >
                    <Edit sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                </label>
                  {profileImage && (
                                  <Button
                                    variant="contained"
                                    onClick={uploadProfileImage}
                                    disabled={uploading}
                                    fullWidth
                                    sx={{ mb: 1 }}
                                  >
                                    {uploading ? 'Uploading...' : 'Save Photo'}
                                  </Button>
                                )}
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: '#111827', fontSize: '1rem' }}>
                {profileData.name || 'Profile Name'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
                ID: {profileData.code}
              </Typography>
              
              <Divider sx={{ my: 1.5 }} />
              
              <Box textAlign="left" spacing={1.5}>
                <Box mb={1.5}>
                  <Typography 
                    variant="caption" 
                    display="block" 
                    sx={{ 
                      textTransform: 'uppercase', 
                      fontWeight: 700, 
                      mb: 0.5,
                      fontSize: '0.65rem',
                      letterSpacing: '0.05em',
                      color: '#6b7280'
                    }}
                  >
                    Email Address
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Email sx={{ fontSize: '0.9rem', color: '#800000' }} />
                    <Typography 
                      variant="body2" 
                      component="a" 
                      href={`mailto:${profileData.email}`} 
                      sx={{ 
                        color: '#800000', 
                        textDecoration: 'none',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        wordBreak: 'break-all',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {profileData.email || 'No email'}
                    </Typography>
                  </Box>
                </Box>
                
                <Box mb={1.5}>
                  <Typography 
                    variant="caption" 
                    display="block" 
                    sx={{ 
                      textTransform: 'uppercase', 
                      fontWeight: 700, 
                      mb: 0.5,
                      fontSize: '0.65rem',
                      letterSpacing: '0.05em',
                      color: '#6b7280'
                    }}
                  >
                    Phone Number
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Phone sx={{ fontSize: '0.9rem', color: '#800000' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#111827' }}>
                      {profileData.phone || 'No phone'}
                    </Typography>
                  </Box>
                </Box>
                
                <Box mb={1.5}>
                  <Typography 
                    variant="caption" 
                    display="block" 
                    sx={{ 
                      textTransform: 'uppercase', 
                      fontWeight: 700, 
                      mb: 0.5,
                      fontSize: '0.65rem',
                      letterSpacing: '0.05em',
                      color: '#6b7280'
                    }}
                  >
                    Location
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <LocationOn sx={{ fontSize: '0.9rem', color: '#800000' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#111827' }}>
                      {profileData.city || 'No location'}
                    </Typography>
                  </Box>
                </Box>
                
                <Box>
                  <Typography 
                    variant="caption" 
                    display="block" 
                    sx={{ 
                      textTransform: 'uppercase', 
                      fontWeight: 700, 
                      mb: 0.5,
                      fontSize: '0.65rem',
                      letterSpacing: '0.05em',
                      color: '#6b7280'
                    }}
                  >
                    Status
                  </Typography>
                  <Chip 
                    label={profileData.status || 'Unknown'} 
                    color={getStatusColor(profileData.status)}
                    size="small"
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      height: 22
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
          )}

          {/* Right Side - Tab Content */}
          <Box sx={{ order: { xs: 1, lg: 2 } }}>
            <Fade in={true} timeout={300}>
              <Box>
                {renderTabContent()}
              </Box>
            </Fade>
          </Box>
        </Box>
      </Box>

      {/* Custom Field Builder Modal */}
      <CustomFieldBuilder
        open={customFieldBuilderOpen}
        onClose={() => setCustomFieldBuilderOpen(false)}
        onSave={(fields) => setCustomFields([...customFields, ...fields])}
        entityType={entityType}
      />

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={hideSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;